import React, { useEffect, useState, useCallback, useMemo } from "react";
// 🚀 FIX: Import useSearchParams (or continue using useLocation and useNavigate as you were, but useSearchParams is cleaner)
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaSearch,
  FaFileExport,
  FaEye,
  FaCheck,
  FaTimes,
  FaArrowLeft,
} from "react-icons/fa";
import {
  fetchRecentOrders,
  updateOrderStatus,
  exportOrdersCsv,
} from "../adminApi";

const STATUS_OPTIONS = [
  "all",
  "pending",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "refunded",
];

export default function Orders() {
  const navigate = useNavigate();
  const location = useLocation();

  // --- Filter State (Local State for inputs) ---
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [savingStatusId, setSavingStatusId] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // --- Computed State from URL ---
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  // The actual status used for filtering is ALWAYS read from the URL
  const currentStatusFromUrl = STATUS_OPTIONS.includes(urlParams.get("status"))
    ? urlParams.get("status")
    : "all";

  // --- HANDLERS to update URL and State ---

  // This function reads all parameters, including the status, from the component's state
  // and the URL, and then fetches the data.
  const loadOrders = useCallback(async () => {
    // ⚠️ IMPORTANT: Load all parameters from state or URL

    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: pageSize,
        page,
        q: query || undefined,
        // 🟢 FIX: Use currentStatusFromUrl (the source of truth) for the API call
        status:
          currentStatusFromUrl === "all" ? undefined : currentStatusFromUrl,
        from: from || undefined,
        to: to || undefined,
      };
      const data = await fetchRecentOrders(params);

      if (Array.isArray(data)) {
        setOrders(data);
        setTotal(data.length);
      } else {
        setOrders(data.orders || []);
        setTotal(data.total ?? (data.orders ? data.orders.length : 0));
      }
    } catch (err) {
      console.error("Failed to load orders:", err);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query, currentStatusFromUrl, from, to]); // Dependency array uses currentStatusFromUrl

  // 🟢 FIX 1: New helper to change status and update the URL
  const handleStatusFilterChange = (newStatus) => {
    // 1. Create a new URLSearchParams object from the current URL
    const params = new URLSearchParams(location.search);

    // 2. Set the new status parameter
    if (newStatus && newStatus !== "all") {
      params.set("status", newStatus);
    } else {
      params.delete("status"); // Clean URL when status is 'all'
    }

    // 3. Reset page and navigate (updates the URL, which triggers the useEffect)
    setPage(1);
    navigate({ search: params.toString() }); // Use 'navigate' (not replace) to allow back button
  };

  // 🟢 FIX 2: General handler for date/query changes that resets the page
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(1); // Reset to page 1 whenever filter criteria changes
  };

  // 🟢 FIX 3: Primary useEffect for loading data
  // This runs on mount AND whenever any of the filter dependencies change (which are used in loadOrders)
  useEffect(() => {
    // We explicitly call loadOrders here when its dependencies change (page, query, from, to, currentStatusFromUrl)
    loadOrders();

    // Cleanup function is not needed here
  }, [loadOrders]);

  // --- Other Handlers (No change needed) ---

  const handleView = (orderId) => {
    navigate(`/dashboard-admin/orders/${orderId}`);
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    if (typeof updateOrderStatus !== "function") {
      alert(
        "The 'updateOrderStatus' function is not implemented in adminApi.js. Cannot change status.",
      );
      return;
    }

    if (savingStatusId === orderId) return;

    setSavingStatusId(orderId);
    try {
      await updateOrderStatus(orderId, { status: newStatus });

      // If the current URL filter is not 'all' and the new status is different, remove the order
      if (
        currentStatusFromUrl !== "all" &&
        currentStatusFromUrl !== newStatus
      ) {
        setOrders((prev) => prev.filter((o) => o.order_id !== orderId));
        setTotal((prev) => prev - 1);
      } else {
        // optimistic local update
        setOrders((prev) =>
          prev.map((o) =>
            o.order_id === orderId ? { ...o, status: newStatus } : o,
          ),
        );
      }
    } catch (err) {
      console.error("Failed to update status:", err);
      alert("Failed to update status");
    } finally {
      setSavingStatusId(null);
    }
  };

  const handleExport = async () => {
    if (typeof exportOrdersCsv !== "function") {
      alert(
        "Export not implemented on client. Implement exportOrdersCsv in adminApi.js",
      );
      return;
    }
    setExporting(true);
    try {
      const blob = await exportOrdersCsv({
        from,
        to,
        status:
          currentStatusFromUrl === "all" ? undefined : currentStatusFromUrl,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${from || "all"}_${to || "now"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. See console.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/dashboard-admin")}
            className="text-gray-600 hover:text-gray-900 transition-colors"
            title="Back to Dashboard"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold">Orders</h1>
            <p className="text-sm text-gray-500">
              Manage and review store orders
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={from}
              onChange={handleFilterChange(setFrom)}
              className="border rounded px-3 py-2 text-sm"
            />
            <span className="text-sm text-gray-500">—</span>
            <input
              type="date"
              value={to}
              onChange={handleFilterChange(setTo)}
              className="border rounded px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={() => {
              setFrom("");
              setTo("");
              setPage(1);
              // 🟢 FIX: Call loadOrders to re-fetch without dates
              loadOrders();
            }}
            className="px-3 py-2 border rounded text-sm bg-white hover:bg-gray-50"
            title="Reset dates"
          >
            Reset
          </button>

          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 bg-[#83375B] text-white px-4 py-2 rounded shadow"
            disabled={exporting}
          >
            <FaFileExport />
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow mb-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <input
              type="search"
              placeholder="Search by order number, customer email or name..."
              value={query}
              onChange={handleFilterChange(setQuery)}
              className="w-full border rounded pl-10 pr-3 py-2 text-sm"
            />
            <FaSearch className="absolute left-3 top-2.5 text-gray-400" />
          </div>

          <select
            // 🟢 FIX: Use the status from the URL to drive the select box
            value={currentStatusFromUrl}
            // 🟢 FIX: Use the specific URL-updating handler
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === "all"
                  ? "All statuses"
                  : s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>

          <button
            onClick={loadOrders} // Explicitly call fetch
            className="px-3 py-2 bg-[#83375B] text-white rounded"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b">
              <th className="py-3 px-4">Order</th>
              <th className="py-3 px-4">Customer</th>
              <th className="py-3 px-4">Items</th>
              <th className="py-3 px-4">Total</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Date</th>
              <th className="py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="py-6 text-center text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="py-6 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-6 text-center text-gray-400">
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr key={o.order_id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleView(o.order_id)}
                      className="text-sm text-[#83375B] hover:underline font-medium"
                    >
                      #{o.order_number || o.order_id}
                    </button>
                    <div className="text-xs text-gray-400">
                      {o.shipping?.method || o.payment_method || ""}
                    </div>
                  </td>

                  <td className="py-3 px-4">
                    <div className="font-medium">
                      {o.customer_name || o.customer_email}
                    </div>
                    <div className="text-xs text-gray-500">
                      {o.customer_email}
                    </div>
                  </td>

                  <td className="py-3 px-4">
                    {o.items_count ?? o.items?.length ?? 0}
                  </td>

                  <td className="py-3 px-4 font-semibold">
                    TL {parseFloat(o.total ?? 0).toFixed(2)}
                  </td>

                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <select
                        value={o.status || ""}
                        onChange={(e) =>
                          handleStatusUpdate(o.order_id, e.target.value)
                        }
                        disabled={savingStatusId === o.order_id}
                        className="text-sm border rounded px-2 py-1"
                      >
                        {STATUS_OPTIONS.filter((s) => s !== "all").map((s) => (
                          <option key={s} value={s}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </option>
                        ))}
                      </select>
                      {o.status === "delivered" ? (
                        <FaCheck className="text-green-500" />
                      ) : null}
                      {o.status === "cancelled" ? (
                        <FaTimes className="text-red-500" />
                      ) : null}
                    </div>
                  </td>

                  <td className="py-3 px-4 text-sm text-gray-500">
                    {o.createdAt ? new Date(o.createdAt).toLocaleString() : "-"}
                  </td>

                  <td className="py-3 px-4 text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleView(o.order_id)}
                        className="px-2 py-1 border rounded text-[#533] hover:bg-gray-50 inline-flex items-center gap-2"
                      >
                        <FaEye /> View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* footer / pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-500">
          Showing {orders.length} of {total} orders
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-3 py-1 border rounded text-sm">Page {page}</span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page * pageSize >= total}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
